import { Alert, Button, Card, Collapse, Descriptions, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import api, { panelApi } from '../services/api'
import type { OperationChild, TradingOperation } from '../types/operations'
import CfdOperationRecovery from './CfdOperationRecovery'

const { Text } = Typography
const statuses: Record<string, [string, string]> = {
  accepted: ['已接收', 'blue'], running: ['处理中', 'processing'],
  unknown: ['执行结果未知', 'warning'], applied_unverified: ['已应用，待核验', 'gold'],
  verified: ['券商已核验', 'green'], partial_success: ['部分完成', 'orange'],
  closed_unverified: ['已平仓，原保护证据不足', 'gold'],
  failed: ['失败', 'red'], noop: ['无需执行', 'default'],
}
const actions: Record<string, string> = {
  open: '开仓', cancel: '撤单', full_close: '全平', reduce: '减仓', adjust: '调整保护',
}
const value = (v: unknown): string => v === undefined || v === null ? '—'
  : typeof v === 'object' ? JSON.stringify(v) : String(v)
const time = (v: unknown): string => typeof v === 'string' && dayjs(v).isValid()
  ? dayjs(v).format('MM-DD HH:mm:ss') : '—'
const json = (v: unknown) => <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 340, overflow: 'auto' }}>{JSON.stringify(v, null, 2)}</pre>

function requestSummary(command: Record<string, unknown>) {
  const parts = [actions[String(command.operation)] || '操作']
  if (command.operation === 'reduce') parts.push(`${value(command.close_percentage)}%`)
  if (command.operation === 'open') {
    const signal = (command.signal || {}) as Record<string, unknown>
    parts.push(`${value(signal.ticker)} ${signal.action === 'buy' ? '买入' : signal.action === 'sell' ? '卖出' : value(signal.action)}`)
    if (signal.contracts != null) parts.push(`数量 ${value(signal.contracts)}`)
    if (signal.sl != null) parts.push(`SL ${value(signal.sl)}`)
    if (signal.tp != null) parts.push(`TP ${value(signal.tp)}`)
  }
  if (command.sl_to_entry) parts.push('止损移至入场价')
  else if (command.sl_clear) parts.push('清除止损')
  else if (command.sl_price != null) parts.push(`SL ${value(command.sl_price)}`)
  if (command.tp_to_entry) parts.push('止盈移至入场价')
  else if (command.tp_clear) parts.push('清除止盈')
  else if (command.tp_price != null) parts.push(`TP ${value(command.tp_price)}`)
  return parts.join(' · ')
}

function Status({ status, noop = false }: { status?: string; noop?: boolean }) {
  const [label, color] = statuses[noop ? 'noop' : status || 'unknown'] || [status || '未知', 'default']
  return <Tag color={color}>{label}</Tag>
}

function Details({ operation }: { operation: TradingOperation }) {
  const command = operation.command || {}
  const plan = operation.plan
  const targets = ['origin_request_id', 'signal_id', 'position_id', 'order_id']
    .filter(key => command[key]).map(key => `${key}: ${value(command[key])}`)
  const dependencies = ['depends_on_request_id', 'ordering_after_request_id', 'recovery_of_request_id']
    .filter(key => command[key]).map(key => `${key}: ${value(command[key])}`)
  const evidence = Object.entries(operation).filter(([key]) => ![
    'command', 'plan', 'positions', 'poll_url', 'operation_id', 'operation_contract_version',
    'duplicate', 'retry_allowed', 'created_at', 'updated_at',
  ].includes(key)).reduce<Record<string, unknown>>((result, [key, v]) => ({ ...result, [key]: v }), {})
  const protection = (row: OperationChild, side: 'sl' | 'tp') => {
    const targets = (plan?.positions || []) as OperationChild[]
    const target = targets.find(p => row.position_id != null && String(p.position_id) === String(row.position_id))
      || targets.find(p => row.tier != null && p.tier === row.tier)
    const wanted = target?.[`expected_${side}`] ?? row[`expected_${side}`]
    const observed = row[`observed_${side}`]
    return `${value(wanted)} → ${observed == null ? '待核验' : value(observed)}`
  }
  const childColumns: ColumnsType<OperationChild> = [
    { title: '仓位 / 订单', render: (_, row) => value(row.position_id || row.order_id) },
    { title: '状态', dataIndex: 'status', render: s => <Status status={s} /> },
    { title: '请求变化量', dataIndex: 'requested_volume_changed', render: value },
    { title: '券商回执变化量', dataIndex: 'actual_volume_changed', render: value },
    { title: '剩余量', dataIndex: 'remaining_volume', render: value },
    { title: 'SL 目标 → 券商', render: (_, row) => protection(row, 'sl') },
    { title: 'TP 目标 → 券商', render: (_, row) => protection(row, 'tp') },
    { title: '核验依据', render: (_, row) => value(row.verification_status || row.evidence_source || row.attribution) },
  ]
  return <Space direction="vertical" style={{ width: '100%' }} size="middle">
    <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
      <Descriptions.Item label="请求 ID" span={2}><Text copyable>{operation.request_id}</Text></Descriptions.Item>
      <Descriptions.Item label="目标仓组 / 仓位" span={2}>{targets.join(' · ') || '开仓时创建'}</Descriptions.Item>
      {!!dependencies.length && <Descriptions.Item label="先后关系" span={2}>{dependencies.join(' · ')}</Descriptions.Item>}
      <Descriptions.Item label="原始要求" span={2}>{requestSummary(command)}</Descriptions.Item>
      <Descriptions.Item label="创建时间">{time(operation.created_at)}</Descriptions.Item>
      <Descriptions.Item label="最近更新">{time(operation.updated_at)}</Descriptions.Item>
      <Descriptions.Item label="券商观察时间">{time(operation.observed_at)}</Descriptions.Item>
      <Descriptions.Item label="核验结果">{value(operation.verification_status)}</Descriptions.Item>
      {!!operation.reason && <Descriptions.Item label="说明" span={2}>{operation.reason}</Descriptions.Item>}
      {!!operation.error && <Descriptions.Item label="错误" span={2}><Text type="danger">{operation.error_code}: {operation.error}</Text></Descriptions.Item>}
    </Descriptions>
    {plan ? <Descriptions title="固定执行计划" bordered size="small" column={{ xs: 1, sm: 2 }}>
      <Descriptions.Item label="原持仓量">{value(plan.before_volume)}</Descriptions.Item>
      <Descriptions.Item label="请求减仓比例">{value(plan.requested_close_percentage)}</Descriptions.Item>
      <Descriptions.Item label="实际计划减仓量">{value(plan.effective_close_volume)}</Descriptions.Item>
      <Descriptions.Item label="计划剩余量">{value(plan.effective_remaining_volume)}</Descriptions.Item>
      <Descriptions.Item label="实际计划开仓量">{value(plan.effective_open_volume)}</Descriptions.Item>
      <Descriptions.Item label="实际计划减仓比例">{value(plan.effective_close_percentage)}</Descriptions.Item>
      <Descriptions.Item label="请求 TP">{value(plan.requested_tp)}</Descriptions.Item>
      <Descriptions.Item label="有效 TP">{value(plan.effective_tp)}</Descriptions.Item>
      <Descriptions.Item label="未执行的 TP 档位" span={2}>{value(plan.omitted_tp)}</Descriptions.Item>
    </Descriptions> : <Alert type="info" message="执行计划尚未形成，正在等待目标与券商数据。" />}
    {!!operation.positions?.length && <Table size="small" pagination={false}
      rowKey={(row, index) => `${row.position_id || row.order_id || 'child'}-${index}`}
      columns={childColumns} dataSource={operation.positions} scroll={{ x: 1000 }} />}
    <Collapse size="small" items={[
      { key: 'command', label: '查看原始请求', children: json(command) },
      { key: 'plan', label: '查看完整固定计划与分档数量', children: json(plan) },
      { key: 'children', label: '查看各子动作回执与证据', children: json(operation.positions) },
      { key: 'evidence', label: '查看状态与订单证据', children: json(evidence) },
    ]} />
  </Space>
}

export default function OperationTimeline({ tokenId, authMode, enableRecovery = false, targetFilter }: {
  tokenId: number; authMode: 'admin' | 'panel' | 'none'; enableRecovery?: boolean
  targetFilter?: { signalId: string; originRequestId?: string }
}) {
  const query = useQuery({
    queryKey: ['token-operations', tokenId, authMode],
    queryFn: () => authMode === 'panel' ? panelApi.getOperations(tokenId) : api.token.getOperations(tokenId),
    enabled: !!tokenId && authMode !== 'none',
    refetchInterval: 8000,
  })
  const columns: ColumnsType<TradingOperation> = [
    { title: '最近更新', dataIndex: 'updated_at', width: 150, render: time },
    { title: '操作', width: 90, render: (_, row) => actions[String(row.command?.operation)] || '操作' },
    { title: '请求 ID', dataIndex: 'request_id', ellipsis: true, render: rid => <Text copyable>{rid}</Text> },
    { title: '状态', width: 165, render: (_, row) => <Status status={row.status} noop={row.effect === 'noop'} /> },
    { title: '券商观察', dataIndex: 'observed_at', width: 150, render: time },
  ]
  const rows = (query.data?.items || []).filter(row => !targetFilter ||
    row.command?.signal_id === targetFilter.signalId || row.plan?.signal_id === targetFilter.signalId ||
    !!targetFilter.originRequestId && (row.request_id === targetFilter.originRequestId || row.command?.origin_request_id === targetFilter.originRequestId))
  return <Card title="操作执行记录" size="small" extra={<Button size="small" onClick={() => void query.refetch()} loading={query.isFetching}>刷新</Button>}>
    <Text type="secondary">展示最近 50 条操作；展开可核对原始要求、固定计划与券商证据。</Text>
    {query.isError && <Alert style={{ marginTop: 12 }} type="warning" showIcon message="操作记录暂时无法同步，已保留上次结果。" />}
    <Table rowKey="request_id" size="small" columns={columns} dataSource={rows}
      loading={query.isPending && authMode !== 'none'} pagination={{ pageSize: 10, showSizeChanger: false }}
      scroll={{ x: 720 }} expandable={{ expandedRowRender: row => <Space direction="vertical" style={{ width: '100%' }}><Details operation={row} />{enableRecovery && <CfdOperationRecovery tokenId={tokenId} authMode={authMode} operation={row} />}</Space> }}
      locale={{ emptyText: '暂无 v2 操作记录' }} style={{ marginTop: 12 }} />
  </Card>
}
